import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import '@fontsource/space-grotesk/500.css';
import './play-chrome.css';
import './style.css';
import { AI_CONFIGS } from './ai';
import { GameEngine } from './GameEngine';
import { GameRenderer } from './GameRenderer';
import { loadGame, loadSettings, saveGame, saveSettings, withSharedTheme } from './Persistence';
import { isGameOver, squareToIndex } from './rules';
import type { AiLevel, AiResponse, Color, GameSettings, Move, PromotionKind } from './types';

function element<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing #${id}`);
  return node as T;
}

const canvas = element<HTMLCanvasElement>('game-canvas');
const turnNode = element('turn');
const moveCountNode = element('move-count');
const scoreNode = element('score');
const pauseButton = element<HTMLButtonElement>('pause-button');
const menuButton = element<HTMLButtonElement>('menu-button');
const gameHint = element('game-hint');
const notification = element('notification');
const notificationMessage = element('notification-message');
const lastEvent = element('last-event');
const moveList = element<HTMLOListElement>('move-list');
const gameStatus = element('game-status');
const themeColor = element<HTMLMetaElement>('theme-color');
const settingsDialog = element<HTMLDialogElement>('settings-dialog');
const setupDialog = element<HTMLDialogElement>('setup-dialog');
const setupForm = element<HTMLFormElement>('setup-form');
const setupCancel = element<HTMLButtonElement>('setup-cancel');
const settingsNewGame = element<HTMLButtonElement>('settings-new-game');
const promotionDialog = element<HTMLDialogElement>('promotion-dialog');
const soundToggle = element<HTMLInputElement>('sound-toggle');
const hapticToggle = element<HTMLInputElement>('haptic-toggle');
const motionToggle = element<HTMLInputElement>('motion-toggle');
const nightToggle = element<HTMLInputElement>('night-toggle');

const engine = new GameEngine();
const renderer = new GameRenderer(canvas);
let settings: GameSettings = loadSettings();
let selectedSquare: string | null = null;
let selectedMoves: Move[] = [];
let pendingPromotion: { from: string; to: string } | null = null;
let notificationTimer = 0;
let thinking = false;
let aiRequestId = 0;
let aiWorker: Worker | null = null;
let audioContext: AudioContext | null = null;
let pointerState: { id: number; startSquare: string | null; startX: number; startY: number; moved: boolean } | null = null;

function randomSeed(): number {
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return values[0] ?? Math.floor(Date.now() / 1000);
}

function showNotification(message: string, duration = 1_800): void {
  notificationMessage.textContent = message;
  notification.classList.add('visible');
  window.clearTimeout(notificationTimer);
  notificationTimer = window.setTimeout(() => notification.classList.remove('visible'), duration);
}

function vibrate(pattern: number | number[]): void {
  if (settings.haptic && 'vibrate' in navigator) navigator.vibrate(pattern);
}

function unlockAudio(): void {
  if (!settings.sound) return;
  audioContext ??= new AudioContext();
  if (audioContext.state === 'suspended') void audioContext.resume();
}

function playTone(frequency: number, duration = 0.07): void {
  if (!settings.sound || !audioContext) return;
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();
  oscillator.type = 'square';
  oscillator.frequency.value = frequency;
  gain.gain.setValueAtTime(0.025, audioContext.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + duration);
  oscillator.connect(gain).connect(audioContext.destination);
  oscillator.start();
  oscillator.stop(audioContext.currentTime + duration);
}

function persist(): void {
  try {
    saveGame(engine.state, settings);
  } catch (error) {
    console.warn('Could not save Infinite Chess', error);
  }
}

function applySettings(): void {
  renderer.flipped = settings.playerColor === 'b' ? !settings.flipped : settings.flipped;
  renderer.nightMode = settings.nightMode;
  renderer.reducedMotion = settings.reducedMotion;
  document.documentElement.classList.toggle('night-mode', settings.nightMode);
  document.documentElement.classList.toggle('reduced-motion', settings.reducedMotion);
  themeColor.content = settings.nightMode ? '#151713' : '#f6f2e9';
  saveSettings(settings);
  syncSettingsForm();
}

function syncSettingsForm(): void {
  for (const input of document.querySelectorAll<HTMLInputElement>('input[name="settings-ai-level"]')) {
    input.checked = Number(input.value) === settings.aiLevel;
  }
  soundToggle.checked = settings.sound;
  hapticToggle.checked = settings.haptic;
  motionToggle.checked = settings.reducedMotion;
  nightToggle.checked = settings.nightMode;
}

function syncSetupForm(): void {
  for (const input of document.querySelectorAll<HTMLInputElement>('input[name="player-color"]')) {
    input.checked = input.value === settings.playerColor;
  }
  for (const input of document.querySelectorAll<HTMLInputElement>('input[name="setup-ai-level"]')) {
    input.checked = Number(input.value) === settings.aiLevel;
  }
}

function statusLabel(): string {
  const state = engine.state;
  if (thinking) return 'AI THINKING';
  if (state.paused) return 'PAUSED';
  if (state.status === 'checkmate') return `${state.winner === 'w' ? 'WHITE' : 'BLACK'} WINS`;
  if (state.status === 'stalemate') return 'STALEMATE';
  if (state.status === 'draw-repetition') return 'DRAW / REPETITION';
  if (state.status === 'draw-fifty') return 'DRAW / 50 MOVE';
  if (state.status === 'draw-material') return 'DRAW / MATERIAL';
  if (state.status === 'check') return state.turn === settings.playerColor ? 'CHECK / YOUR MOVE' : 'CHECK / AI MOVE';
  return state.turn === settings.playerColor ? 'YOUR MOVE' : 'AI TURN';
}

function updateMoveList(): void {
  moveList.replaceChildren();
  for (const record of engine.state.moveHistory) {
    const item = document.createElement('li');
    const color = document.createElement('strong');
    color.textContent = record.color === 'w' ? 'W ' : 'B ';
    item.append(color, document.createTextNode(record.notation));
    if (record.scoreGain !== undefined) item.append(document.createTextNode(` / +${record.scoreGain} X${record.scoreMultiplier ?? 1}`));
    if (record.resurrected) item.append(document.createTextNode(` / RETURN ${record.resurrected.square}`));
    moveList.append(item);
  }
}

function updateHud(): void {
  const state = engine.state;
  turnNode.textContent = state.turn === 'w' ? 'WHITE' : 'BLACK';
  moveCountNode.textContent = String(state.fullmoveNumber);
  scoreNode.textContent = `W ${state.whiteScore} / B ${state.blackScore}`;
  gameStatus.textContent = statusLabel();
  gameHint.textContent = selectedSquare
    ? 'CHOOSE A DESTINATION'
    : state.paused
      ? 'PRESS RESUME TO CONTINUE'
      : state.turn === settings.playerColor
        ? 'SELECT A PIECE TO MOVE'
        : 'THE AI IS CALCULATING';
  pauseButton.textContent = state.paused ? 'RESUME' : 'PAUSE';
  pauseButton.setAttribute('aria-label', state.paused ? 'Resume game' : 'Pause game');
  if (state.lastResurrection) {
    const piece = state.lastResurrection.piece.kind.toUpperCase();
    const original = state.lastResurrection.originalKind.toUpperCase();
    const score = state.lastMove?.scoreGain !== undefined
      ? ` / +${state.lastMove.scoreGain} X${state.lastMove.scoreMultiplier ?? 1}`
      : '';
    lastEvent.textContent = `RETURN / ${original} BECAME ${piece} AT ${state.lastResurrection.square.toUpperCase()}${score}`;
  } else if (state.lastMove?.capture) {
    lastEvent.textContent = `LAST CAPTURE / +${state.lastMove.scoreGain ?? 0} X${state.lastMove.scoreMultiplier ?? 1} / ${state.lastMove.color === 'w' ? 'WHITE' : 'BLACK'} STREAK ${state.lastMove.captureStreak ?? 1}`;
  } else if (state.lastMove) {
    lastEvent.textContent = `LAST MOVE / ${state.lastMove.notation}`;
  } else {
    lastEvent.textContent = 'THE BOARD REMEMBERS EVERY RETURN';
  }
  updateMoveList();
}

function render(): void {
  renderer.render(engine.state, settings.playerColor, selectedSquare, selectedMoves, thinking);
}

function closeSelection(): void {
  selectedSquare = null;
  selectedMoves = [];
}

function selectPiece(square: string): void {
  const index = squareToIndex(square);
  if (index === null) return;
  const piece = engine.state.board[index];
  if (!piece || piece.color !== settings.playerColor || engine.state.turn !== settings.playerColor) return;
  const moves = engine.legalMovesFrom(square);
  if (!moves.length) {
    showNotification('NO LEGAL MOVE');
    return;
  }
  selectedSquare = square;
  selectedMoves = moves;
}

function commitMove(move: Move): void {
  unlockAudio();
  const result = engine.move(move);
  if (!result.ok) {
    showNotification(result.reason ?? 'ILLEGAL MOVE');
    return;
  }
  closeSelection();
  vibrate(result.resurrected ? [10, 24, 10] : 8);
  playTone(result.resurrected ? 520 : result.captured ? 240 : 340, result.resurrected ? 0.12 : 0.06);
  if (result.resurrected) {
    const returned = result.resurrected.piece.kind.toUpperCase();
    showNotification(`RESURRECTED / ${returned} AT ${result.resurrected.square.toUpperCase()} / +${result.scoreGain ?? 0} X${result.scoreMultiplier ?? 1}`, 2_800);
  } else if (result.captured) {
    showNotification(`CAPTURE / ${result.captured.kind.toUpperCase()} +${result.scoreGain ?? 0} X${result.scoreMultiplier ?? 1}`, 1_600);
  }
  persist();
  updateHud();
  render();
  if (!isGameOver(engine.state) && !engine.state.paused && engine.state.turn !== settings.playerColor) {
    window.setTimeout(startAi, 120);
  }
}

function attemptDestination(square: string): void {
  if (!selectedSquare) return;
  const choices = selectedMoves.filter((move) => move.to === square);
  if (!choices.length) {
    const index = squareToIndex(square);
    const piece = index === null ? null : engine.state.board[index];
    if (piece?.color === settings.playerColor) selectPiece(square);
    else showNotification('ILLEGAL MOVE', 1_100);
    return;
  }
  if (choices.length > 1) {
    pendingPromotion = { from: choices[0]!.from, to: choices[0]!.to };
    if (!promotionDialog.open) promotionDialog.showModal();
    return;
  }
  commitMove(choices[0]!);
}

function handleSquare(square: string): void {
  if (thinking || engine.state.paused || isGameOver(engine.state) || engine.state.turn !== settings.playerColor) return;
  if (!selectedSquare) {
    selectPiece(square);
    updateHud();
    render();
    return;
  }
  if (square === selectedSquare) {
    closeSelection();
    updateHud();
    render();
    return;
  }
  attemptDestination(square);
  updateHud();
  render();
}

function stopAi(): void {
  aiRequestId += 1;
  aiWorker?.terminate();
  aiWorker = null;
  thinking = false;
}

function handleAiResponse(response: AiResponse): void {
  if (response.requestId !== aiRequestId || !thinking) return;
  thinking = false;
  aiWorker?.terminate();
  aiWorker = null;
  if (response.type !== 'bestmove' || !response.move) {
    showNotification('AI COULD NOT FIND A MOVE');
    updateHud();
    render();
    return;
  }
  const result = engine.move(response.move);
  if (!result.ok) {
    const fallback = engine.legalMoves()[0];
    if (fallback) commitMove(fallback);
    else {
      updateHud();
      render();
    }
    return;
  }
  if (result.resurrected) {
    showNotification(`AI RETURN / ${result.resurrected.piece.kind.toUpperCase()} AT ${result.resurrected.square.toUpperCase()} / +${result.scoreGain ?? 0} X${result.scoreMultiplier ?? 1}`, 2_300);
    playTone(470, 0.1);
  } else {
    if (result.captured) showNotification(`AI CAPTURE / ${result.captured.kind.toUpperCase()} +${result.scoreGain ?? 0} X${result.scoreMultiplier ?? 1}`, 1_700);
    playTone(result.captured ? 220 : 300, 0.05);
  }
  persist();
  updateHud();
  render();
}

function startAi(): void {
  if (thinking || engine.state.paused || isGameOver(engine.state) || engine.state.turn === settings.playerColor) return;
  thinking = true;
  const requestId = ++aiRequestId;
  const level: AiLevel = settings.aiLevel;
  aiWorker?.terminate();
  aiWorker = new Worker(new URL('./ai.worker.ts', import.meta.url), { type: 'module' });
  aiWorker.addEventListener('message', (event: MessageEvent<AiResponse>) => handleAiResponse(event.data));
  aiWorker.addEventListener('error', () => {
    if (requestId !== aiRequestId) return;
    thinking = false;
    aiWorker?.terminate();
    aiWorker = null;
    showNotification('AI ERROR / TRY AGAIN');
    updateHud();
    render();
  }, { once: true });
  aiWorker.postMessage({ type: 'search', requestId, state: engine.state, level });
  updateHud();
  render();
}

function openSetup(): void {
  stopAi();
  syncSetupForm();
  if (!setupDialog.open) setupDialog.showModal();
}

function newGame(color: Color, aiLevel: AiLevel): void {
  stopAi();
  settings.playerColor = color;
  settings.aiLevel = aiLevel;
  settings.flipped = false;
  applySettings();
  engine.newGame(randomSeed());
  closeSelection();
  persist();
  showNotification(`NEW GAME / ${color === 'w' ? 'WHITE' : 'BLACK'} / AI ${aiLevel}`, 2_200);
  updateHud();
  render();
  if (color === 'b') window.setTimeout(startAi, 150);
}

function setupInputValue(name: string): string {
  return document.querySelector<HTMLInputElement>(`input[name="${name}"]:checked`)?.value ?? '';
}

setupForm.addEventListener('submit', (event) => {
  event.preventDefault();
  const color = setupInputValue('player-color') as Color;
  const level = Number(setupInputValue('setup-ai-level')) as AiLevel;
  setupDialog.close();
  newGame(color === 'b' ? 'b' : 'w', AI_CONFIGS[level] ? level : 1200);
});
setupCancel.addEventListener('click', () => setupDialog.close());

// Closing the picker (CLOSE or Escape) cancels the pending promotion; the pawn stays selected.
promotionDialog.addEventListener('close', () => { pendingPromotion = null; });
promotionDialog.querySelectorAll<HTMLButtonElement>('[data-promotion]').forEach((button) => {
  button.addEventListener('click', () => {
    if (!pendingPromotion) return;
    const promotion = button.dataset.promotion as PromotionKind;
    const move: Move = { ...pendingPromotion, promotion };
    pendingPromotion = null;
    promotionDialog.close();
    commitMove(move);
  });
});

canvas.addEventListener('pointerdown', (event) => {
  if (!event.isPrimary) return;
  pointerState = {
    id: event.pointerId,
    startSquare: renderer.clientToSquare(event.clientX, event.clientY),
    startX: event.clientX,
    startY: event.clientY,
    moved: false,
  };
  canvas.setPointerCapture(event.pointerId);
});
canvas.addEventListener('pointermove', (event) => {
  if (!pointerState || pointerState.id !== event.pointerId) return;
  pointerState.moved = Math.hypot(event.clientX - pointerState.startX, event.clientY - pointerState.startY) > 8;
});
canvas.addEventListener('pointerup', (event) => {
  if (!pointerState || pointerState.id !== event.pointerId) return;
  event.preventDefault();
  const current = pointerState;
  pointerState = null;
  if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
  unlockAudio();
  const targetSquare = renderer.clientToSquare(event.clientX, event.clientY);
  if (!targetSquare) return;
  if (current.moved && current.startSquare && current.startSquare !== targetSquare) {
    if (selectedSquare !== current.startSquare) selectPiece(current.startSquare);
    attemptDestination(targetSquare);
    updateHud();
    render();
    return;
  }
  handleSquare(targetSquare);
});
canvas.addEventListener('pointercancel', (event) => {
  if (pointerState?.id === event.pointerId) pointerState = null;
  if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
});

pauseButton.addEventListener('click', () => {
  unlockAudio();
  if (engine.state.paused) {
    engine.togglePause(false);
    showNotification('GAME RESUMED');
    if (engine.state.turn !== settings.playerColor) window.setTimeout(startAi, 100);
  } else {
    stopAi();
    engine.togglePause(true);
    showNotification('GAME PAUSED');
  }
  persist();
  updateHud();
  render();
});
menuButton.addEventListener('click', () => {
  syncSettingsForm();
  settingsDialog.showModal();
});
settingsNewGame.addEventListener('click', () => {
  settingsDialog.close();
  openSetup();
});

for (const input of document.querySelectorAll<HTMLInputElement>('input[name="settings-ai-level"]')) {
  input.addEventListener('change', () => {
    settings.aiLevel = Number(input.value) as AiLevel;
    saveSettings(settings);
    updateHud();
    showNotification(`AI LEVEL / ${settings.aiLevel}`);
  });
}
soundToggle.addEventListener('change', () => { settings.sound = soundToggle.checked; applySettings(); });
hapticToggle.addEventListener('change', () => { settings.haptic = hapticToggle.checked; applySettings(); });
motionToggle.addEventListener('change', () => { settings.reducedMotion = motionToggle.checked; applySettings(); render(); });
nightToggle.addEventListener('change', () => { settings.nightMode = nightToggle.checked; applySettings(); render(); });

const resizeObserver = new ResizeObserver(() => {
  renderer.resize();
  render();
});
resizeObserver.observe(canvas);

document.addEventListener('visibilitychange', () => {
  if (!document.hidden) return;
  stopAi();
  engine.togglePause(true);
  persist();
  updateHud();
  render();
});
window.addEventListener('pagehide', () => {
  stopAi();
  engine.togglePause(true);
  persist();
});

async function bootstrap(): Promise<void> {
  const save = loadGame();
  if (save) {
    settings = withSharedTheme(save.settings);
    applySettings();
    engine.restore(save.state);
    showNotification('GAME RESTORED / PAUSED', 2_400);
  } else {
    applySettings();
    window.setTimeout(openSetup, 0);
  }
  updateHud();
  render();
  if ('serviceWorker' in navigator) {
    const registerOffline = () => navigator.serviceWorker.register(`${import.meta.env.BASE_URL}sw.js`, {scope: import.meta.env.BASE_URL})
      .catch((error) => console.warn('Offline mode unavailable', error));
    if (document.readyState === 'complete') void registerOffline();
    else window.addEventListener('load', () => void registerOffline(), { once: true });
  }
}

void bootstrap();
